import {
    TextField,
    Grid,
    FormControlLabel,
    RadioGroup,
    Radio,
    Checkbox,
    Typography,
    Select,
    MenuItem,
    FormHelperText,
    Switch,
} from '@mui/material';
import { FormikProps } from 'formik';
import { CustomerTypes } from '../../../../../types';
import { SettingsFieldCard } from './settings-field-card';
import * as S from '../../../../onboarding/customer-registration/styles';

interface Props {
    formik: FormikProps<CustomerTypes.OnboardingForm>;
    /**
     * Gates only the v2 catalog settings on this tab (User Identifier and
     * Minimum App Version). Company, contact, address and phone-type fields
     * live on the Customer document itself — they are not catalog settings and
     * have no v2 editor, so they stay editable regardless. Defaults to
     * disabled so a caller that forgets the prop cannot reopen a legacy
     * settings write path.
     */
    settingsDisabled?: boolean;
}

const parseOptionalNumber = (value: unknown): number | undefined => {
    if (value === '' || value === null || value === undefined) {
        return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

export const GeneralSettingsTab: React.FC<Props> = ({ formik, settingsDisabled = true }) => {
    const { getFieldProps, touched, errors, values } = formik;

    const handleFieldChange = (field: string, value: any) => {
        formik.setFieldValue(field, value);
    };

    const selectMultipleRadio = (
        source: 'phoneOperationSystems',
        value: string
    ) => {
        const sourceValues = formik?.values[source];
        if (sourceValues?.includes(value)) {
            formik.setFieldValue(source, sourceValues?.filter((v) => v !== value));
            return;
        }
        formik.setFieldValue(source, [...(sourceValues ?? []), value]);
    };

    const isSelectedMultipleRadio = (
        source: 'phoneOperationSystems',
        value: string
    ) => {
        return formik?.values[source]?.includes(value);
    };

    return (
        <>
            {/* Company Information */}
            <Typography fontWeight={500} fontSize={18} mt={2}>
                Company Information
            </Typography>
            <Typography fontSize={14} mb={1} color="text.secondary">
                Basic organization details and identity.
            </Typography>

            <TextField
                size="medium"
                placeholder="Organization name"
                fullWidth
                sx={{ mt: 1, height: 70 }}
                {...getFieldProps('companyName')}
                error={Boolean(errors.companyName)}
                helperText={touched.companyName && errors.companyName}
            />

            {/* Shipping Information */}
            <Typography fontWeight={500} mt={4} mb={1} fontSize={18}>
                Shipping Information
            </Typography>
            <Typography fontSize={14} mb={2} color="text.secondary">
                Primary contact and shipping address for this organization.
            </Typography>

            <Typography fontWeight={400} fontSize={16} mb={2}>
                Contact Name
            </Typography>
            <Grid container spacing={3}>
                <Grid item xl={4} md={4} sm={12} xs={12}>
                    <TextField
                        fullWidth
                        size="medium"
                        placeholder="First"
                        {...getFieldProps('contactFirstName')}
                        error={Boolean(touched.contactFirstName && errors.contactFirstName)}
                        helperText={touched.contactFirstName && errors.contactFirstName}
                    />
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                    <TextField
                        fullWidth
                        size="medium"
                        placeholder="Last"
                        {...getFieldProps('contactLastName')}
                        error={Boolean(touched.contactLastName && errors.contactLastName)}
                        helperText={touched.contactLastName && errors.contactLastName}
                    />
                </Grid>
                <Grid item md={12} sm={12} xs={12}>
                    <TextField
                        fullWidth
                        size="medium"
                        placeholder="Street Address"
                        {...getFieldProps('streetAddress')}
                        error={Boolean(touched.streetAddress && errors.streetAddress)}
                        helperText={touched.streetAddress && errors.streetAddress}
                    />
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                    <TextField
                        fullWidth
                        size="medium"
                        placeholder="City"
                        {...getFieldProps('cityAddress')}
                        error={Boolean(touched.cityAddress && errors.cityAddress)}
                        helperText={touched.cityAddress && errors.cityAddress}
                    />
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                    <TextField
                        fullWidth
                        size="medium"
                        placeholder="State"
                        {...getFieldProps('stateAddress')}
                        error={Boolean(touched.stateAddress && errors.stateAddress)}
                        helperText={touched.stateAddress && errors.stateAddress}
                    />
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                    <TextField
                        fullWidth
                        size="medium"
                        type="number"
                        placeholder="Zipcode"
                        {...getFieldProps('zipcodeAddress')}
                        onChange={(e) => handleFieldChange('zipcodeAddress', parseOptionalNumber(e.target.value))}
                        error={Boolean(touched.zipcodeAddress && errors.zipcodeAddress)}
                        helperText={touched.zipcodeAddress && errors.zipcodeAddress}
                    />
                </Grid>
            </Grid>

            {/* Phone Type */}
            <Typography fontWeight={500} mb={1} fontSize={18} mt={4}>
                Phone Type
            </Typography>
            <Typography fontSize={14} mb={2} color="text.secondary">
                Device ownership and operating system information for this organization's team members.
            </Typography>

            <Typography fontWeight={400} fontSize={16}>
                Phone Ownership
            </Typography>
            <RadioGroup {...getFieldProps('phoneOwnership')}>
                <Grid container spacing={2}>
                    <Grid item md={2} sm={12} xs={12}>
                        <FormControlLabel
                            sx={S.RadioBoxStyle}
                            value="personal"
                            control={<Radio />}
                            label="Personal"
                        />
                    </Grid>
                    <Grid item md={2} sm={12} xs={12}>
                        <FormControlLabel
                            sx={S.RadioBoxStyle}
                            value="company"
                            control={<Radio />}
                            label="Company"
                        />
                    </Grid>
                </Grid>
            </RadioGroup>
            <Typography mt={1} color={'red'} fontWeight={400}>
                {errors?.phoneOwnership}
            </Typography>

            <Typography fontWeight={400} fontSize={16} mt={3}>
                Phone Operating Systems
            </Typography>
            <Grid container spacing={2} mt={0.5}>
                <Grid item md={2} sm={12} xs={12}>
                    <FormControlLabel
                        sx={S.RadioBoxStyle}
                        value="ios"
                        control={
                            <Checkbox
                                onClick={() => selectMultipleRadio('phoneOperationSystems', 'ios')}
                                checked={isSelectedMultipleRadio('phoneOperationSystems', 'ios')}
                            />
                        }
                        label="iOS"
                    />
                </Grid>
                <Grid item md={2} sm={12} xs={12}>
                    <FormControlLabel
                        sx={S.RadioBoxStyle}
                        value="android"
                        control={
                            <Checkbox
                                onClick={() => selectMultipleRadio('phoneOperationSystems', 'android')}
                                checked={isSelectedMultipleRadio('phoneOperationSystems', 'android')}
                            />
                        }
                        label="Android"
                    />
                </Grid>
            </Grid>

            {/* User Identifier */}
            <Typography fontWeight={500} mt={4} fontSize={18}>
                User Management
            </Typography>
            <Typography fontSize={14} mb={1} color="text.secondary">
                Define how preregistered users authenticate into this organization.
            </Typography>

            <SettingsFieldCard>
                <Grid maxWidth="sm" sx={{ width: 2 / 3 }}>
                    <Typography fontWeight="600" fontSize={16}>User Identifier</Typography>
                    <Typography color="textSecondary" variant="body2">
                        How preregistered users authenticate into the organization. Options: invite code, employee ID, or email address.
                    </Typography>
                </Grid>
                <Grid item sx={{ mt: 1 }}>
                    <Select
                        {...getFieldProps('userUniqueIdentifier')}
                        value={values?.userUniqueIdentifier || 'INVITECODE'}
                        onChange={(e) => handleFieldChange('userUniqueIdentifier', e.target.value)}
                        disabled={settingsDisabled}
                        sx={{ width: 200 }}
                        error={touched.userUniqueIdentifier && Boolean(errors.userUniqueIdentifier)}
                    >
                        <MenuItem value="INVITECODE">Invite Code</MenuItem>
                        <MenuItem value="EMPLOYEEID">Employee ID</MenuItem>
                        <MenuItem value="EMAILADDRESS">Email</MenuItem>
                    </Select>
                    <FormHelperText>
                        {touched.userUniqueIdentifier && errors.userUniqueIdentifier}
                    </FormHelperText>
                </Grid>
            </SettingsFieldCard>

            {/* Minimum App Version */}
            <SettingsFieldCard>
                <Grid maxWidth="sm" sx={{ width: 2 / 3 }}>
                    <Typography fontWeight="600" fontSize={16}>Minimum App Version</Typography>
                    <Typography color="textSecondary" variant="body2">
                        The minimum mobile app version required for users in this organization. Users on older versions will be prompted to update.
                    </Typography>
                </Grid>
                <TextField
                    sx={{ width: 150, my: "auto" }}
                    size="medium"
                    type="text"
                    placeholder="e.g. 2.1.0"
                    value={values?.minAppVersion || ''}
                    onChange={(e) => handleFieldChange('minAppVersion', e.target.value)}
                    disabled={settingsDisabled}
                    InputProps={{ readOnly: settingsDisabled }}
                />
            </SettingsFieldCard>
        </>
    );
};
